import { type ComponentProps, type ReactNode } from "react";
import { motion } from "framer-motion";

import Eyebrow from "@/blueprint/components/Eyebrow/Eyebrow";
import Text from "@/blueprint/components/Text/Text";
import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";
import AddToCartButton from "@/components/common/AddToCartButton";
import RollingValue from "@/blueprint/components/RollingValue/RollingValue";
import {
  MotionDuration,
  MotionEasing,
  seconds,
} from "@/blueprint/tokens/Motion";
import { cn } from "@/lib/utils";
import { BUTTON_VARIANT_CLASSES } from "@/blueprint/components/Button/ButtonVariant";
import { formatPrice } from "@/utils/ShopifyHelpers";
import { CONFIGURATOR } from "@/views/shop/shopContent";
import type { EventMap } from "@/analytics/events";

/*
 * /product's checkout: the bar pinned to the viewport's foot below `sd`
 * (whose button becomes the inline CTA from `sd`), and the desktop bar.
 */

/**
 * Room at the page's foot for the pinned bars, so the last of the footer
 * still scrolls clear of them. The bars' height, --pinned-bar-height, is set
 * per breakpoint in globals.scss once a bar carries this marker.
 */
export const PINNED_BAR_CLEARANCE =
  "pb-[calc(var(--pinned-bar-height)+env(safe-area-inset-bottom))]";
const PINNED_BAR_MARKER = { "data-pinned-purchase-bar": "" };
/** Marks a bar carrying a notice row below `tab`, which globals.scss adds
    to --pinned-bar-height there. */
const PINNED_BAR_NOTICE_MARKER = { "data-pinned-bar-notice": "" };

export type CheckoutTotal = {
  amount: number | null;
  current: string;
  currencyCode: string;
  /** Small words before a quoted price ("From"), in the line-under style. */
  lead?: string;
};

/** What the pinned bars say about the pick, beside the price. */
export type BarSummary = {
  title: string;
  detail: string | null;
  note: string | null;
};

export type CheckoutCart = Pick<
  ComponentProps<typeof AddToCartButton>,
  | "productId"
  | "quantity"
  | "addOnLines"
  | "attributes"
  | "cartAttributes"
  | "availableForSale"
>;

const TotalPrice = ({
  total,
  surface,
}: {
  total: CheckoutTotal;
  /** `cta`: inside the dark inline button; `bar`: on a white bar. */
  surface: "cta" | "bar";
}) => {
  const onBar = surface === "bar";
  const toneClass = onBar ? "text-content-primary" : "text-tint-white-70";

  if (total.amount === null) {
    return (
      <span className="inline-flex items-baseline gap-[4px] whitespace-nowrap">
        {total.lead !== undefined && (
          <Text
            variant="paragraph-sm"
            tone={onBar ? "secondary" : "inherit"}
            as="span"
            className={cn("font-normal", !onBar && "text-tint-white-70")}
          >
            {total.lead}
          </Text>
        )}
        <span className={toneClass}>{total.current}</span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-baseline whitespace-nowrap",
        onBar ? "gap-[4px] d:gap-xs" : "gap-xs",
      )}
    >
      <span aria-hidden="true" className={toneClass}>
        <RollingValue value={formatPrice(total.amount, total.currencyCode)} />
      </span>
      <VisuallyHidden>{total.current}</VisuallyHidden>
    </span>
  );
};

/** The bars' model line: the model, then the package quieter after a dot. */
export const BarTitle = ({ summary }: { summary: BarSummary }) => (
  <>
    {summary.title}
    {summary.detail === null ? null : (
      <>
        {" "}
        <span
          aria-hidden="true"
          className="mx-xs inline-block text-tint-black-70 [transform:scale(1.3)]"
        >
          ·
        </span>{" "}
        <span className="text-tint-black-70">{summary.detail}</span>
      </>
    )}
  </>
);

/** The model line and its note, leading both bars. */
const BarSummaryText = ({
  summary,
  className,
}: {
  summary: BarSummary;
  className: string;
}) => (
  <div className={cn("min-w-0 flex-col gap-[4px]", className)}>
    {/* Alone, the title's box centres on the button but its letters don't:
        with no descenders they sit high, so they come down 2px. */}
    <Text
      variant="mini-subtitle"
      as="p"
      className={cn(
        "truncate text-sticky-bar-title",
        summary.note === null && "translate-y-[2px]",
      )}
    >
      <BarTitle summary={summary} />
    </Text>
    {summary.note !== null && (
      <Text
        variant="paragraph-sm"
        tone="secondary"
        as="p"
        className="text-sticky-bar-note"
      >
        {summary.note}
      </Text>
    )}
  </div>
);

/**
 * Below `sd`, Tesla's white bar fixed to the viewport's foot for the whole
 * page (tesla.com/model3/design): the model line from `tab`, the price and
 * how it's paid, and the CTA. From `sd` the bar dissolves and its button is
 * the inline CTA beside the options, carrying the total.
 */
export const StickyCheckout = ({
  total,
  barTotal,
  summary,
  paymentNote,
  cart,
  buttonLocation,
  onBeforeAdd,
  notice,
  className,
}: {
  /** The plain total, which the inline CTA carries whatever the pick. */
  total: CheckoutTotal;
  /** The bar's price: it follows the payment pick. */
  barTotal: CheckoutTotal;
  summary: BarSummary;
  /** Under the price: how the payment pick is paid. */
  paymentNote: ReactNode;
  cart: CheckoutCart;
  buttonLocation: EventMap["add_to_cart_clicked"]["button_location"];
  onBeforeAdd: () => void;
  /**
   * A row under the price and the CTA below `tab`, where the model line
   * and its note are hidden (the /lp pages' trial); null for none.
   */
  notice: ReactNode | null;
  /** The /lp pages slide the bar in once the reader scrolls. */
  className?: string;
}) => (
  <div
    {...PINNED_BAR_MARKER}
    {...(notice === null ? {} : PINNED_BAR_NOTICE_MARKER)}
    className={cn(
      "fixed inset-x-0 bottom-0 z-20 bg-brand-white pb-[env(safe-area-inset-bottom)] shadow-bar-lift sd:static sd:bg-transparent sd:py-0 sd:pb-0 sd:shadow-none",
      className,
    )}
  >
    <div className="flex h-[calc(var(--pinned-bar-height)-var(--pinned-bar-notice-height))] items-center gap-s px-l tab:px-[36px] sd:contents">
      <BarSummaryText
        summary={summary}
        className="hidden flex-1 tab:flex sd:hidden"
      />
      <div className="flex min-w-0 flex-1 flex-col tab:flex-none tab:items-end sd:hidden">
        <Text
          variant="paragraph-lg"
          as="span"
          className="text-bar-price font-semibold"
        >
          <TotalPrice total={barTotal} surface="bar" />
        </Text>
        {paymentNote}
      </div>
      <AddToCartButton
        {...cart}
        buttonLocation={buttonLocation}
        onBeforeAdd={onBeforeAdd}
        /* `border-transparent` is load-bearing: the legacy `btn-primary` this
           button carries applies `border-legacy-primary`, a hairline outline
           otherwise. */
        className={cn(
          "flex h-[40px] shrink-0 items-center justify-center rounded-full border-transparent px-l transition-colors disabled:cursor-not-allowed disabled:!border-transparent",
          // Below `sd` the blueprint Button's `solid` look at Tesla's size.
          BUTTON_VARIANT_CLASSES.solid,
          // From `sd` the inline CTA in master's design: the black pill, blue
          // on hover. The solid variant's ring and disabled shell stop there.
          "sd:h-[56px] sd:w-full sd:bg-brand-black sd:px-[24px] sd:text-brand-white sd:ring-0 sd:hover:border-brand-blue sd:hover:bg-brand-blue sd:hover:text-brand-white sd:hover:ring-0 sd:active:bg-brand-blue sd:active:text-brand-white sd:active:ring-0 sd:disabled:!bg-brand-black sd:disabled:text-brand-white sd:disabled:opacity-50 sd:disabled:ring-0",
        )}
      >
        <Eyebrow
          as="span"
          variant="eyebrow-md"
          tone="inherit"
          className="flex items-center gap-xs whitespace-nowrap text-sticky-bar-cta sd:text-eyebrow-md"
        >
          <span>{CONFIGURATOR.ctaLabel}</span>
          {/* The total rides the inline CTA; below `sd` the bar carries it. */}
          <span className="hidden sd:flex">
            <TotalPrice total={total} surface="cta" />
          </span>
        </Eyebrow>
      </AddToCartButton>
    </div>
    {notice === null ? null : (
      <div className="flex h-[var(--pinned-bar-notice-height)] items-start px-l tab:hidden">
        {notice}
      </div>
    )}
  </div>
);

/**
 * From `sd`, a full-width white bar fixed to the viewport's foot (Figma
 * Sticky Checkout Bar 10679:59087) for the whole page: the model line, the
 * price and how it's paid, and the CTA.
 */
export const DesktopStickyBar = ({
  summary,
  total,
  paymentNote,
  reducedMotion,
  cart,
  onBeforeAdd,
  buttonLocation = "shop_sticky_bar",
}: {
  summary: BarSummary;
  total: CheckoutTotal;
  /** Under the total: how the payment pick is paid. */
  paymentNote: ReactNode;
  reducedMotion: boolean;
  cart: CheckoutCart;
  onBeforeAdd: () => void;
  /** /product's own by default; the /lp pages report theirs. */
  buttonLocation?: EventMap["add_to_cart_clicked"]["button_location"];
}) => (
  <motion.div
    // Up from the foot and back down it, the same way, so the bar reads as
    // one surface parked below the page. The exit is quicker than the
    // arrival and owes no settle; it only plays where a caller unmounts the
    // bar inside AnimatePresence (the /lp pages, at the page's top). The
    // transform string keeps it on the compositor while the page scrolls.
    // Reduced motion trades the slide for a short fade. Each state sets both
    // opacity and position: usePrefersReducedMotion reads true until the
    // client knows, so a bar mounted at load (/product's) takes the faded
    // start and must still be brought to full opacity.
    initial={
      reducedMotion
        ? { opacity: 0, transform: "translateY(0%)" }
        : { opacity: 1, transform: "translateY(100%)" }
    }
    animate={{ opacity: 1, transform: "translateY(0%)" }}
    exit={
      reducedMotion
        ? {
            opacity: 0,
            transition: { duration: seconds(MotionDuration.quickMs) },
          }
        : {
            transform: "translateY(100%)",
            transition: {
              duration: seconds(MotionDuration.quickMs),
              ease: [...MotionEasing.outStrong],
            },
          }
    }
    transition={
      reducedMotion
        ? { duration: seconds(MotionDuration.quickMs) }
        : {
            duration: seconds(MotionDuration.mediumMs),
            ease: [...MotionEasing.expoOut],
          }
    }
    className="fixed inset-x-0 bottom-0 z-20 hidden sd:block"
  >
    {/* The surface stops at the page's content cap (max-w-container), so past
        2000px the bar ends where the page does. */}
    <div className="mx-auto flex h-[var(--pinned-bar-height)] max-w-container items-center justify-between bg-brand-white px-grid-margin">
      <BarSummaryText summary={summary} className="flex" />
      <div className="flex shrink-0 items-center gap-[20px] d:gap-l">
        <span className="flex flex-col items-end">
          <Text
            variant="mini-subtitle"
            as="span"
            className="flex h-[26px] items-center d:h-[28px]"
          >
            <TotalPrice total={total} surface="bar" />
          </Text>
          {paymentNote}
        </span>
        <AddToCartButton
          {...cart}
          buttonLocation={buttonLocation}
          onBeforeAdd={onBeforeAdd}
          /* `border-transparent` is load-bearing, as on the sub-desktop bar. */
          className="flex h-[40px] shrink-0 items-center justify-center rounded-full border-transparent bg-brand-black px-l text-brand-white transition-colors hover:border-brand-blue hover:bg-brand-blue disabled:cursor-not-allowed disabled:!bg-brand-black disabled:opacity-50"
        >
          <Eyebrow
            as="span"
            variant="eyebrow-md"
            tone="inherit"
            className="whitespace-nowrap text-sticky-bar-cta"
          >
            {CONFIGURATOR.ctaLabel}
          </Eyebrow>
        </AddToCartButton>
      </div>
    </div>
  </motion.div>
);
